import React from "react";
import { useToast } from "@/hooks/use-toast";
import { submitInquiry } from "@/lib/api/inquiries";
import { MultiStepForm, type ServiceType, type ContactData } from "./multi-step";

const ContactForm = () => {
  const { toast } = useToast();

  const handleSubmit = async (data: {
    serviceType: ServiceType;
    answers: Record<string, unknown>;
    contact: ContactData;
  }): Promise<boolean> => {
    try {
      // Map serviceType to category for database
      // Database only allows: 'join-commons', 'studio-project', 'labs-project', 'partnership', 'general'
      const categoryMap: Record<ServiceType, string> = {
        project: "studio-project",
        partnership: "partnership",
        consultation: "general", // No consultation category in DB, map to general
        general: "general",
      };

      // Prepare the inquiry data for Supabase
      const inquiryData = {
        name: data.contact.name,
        email: data.contact.email,
        phone: data.contact.phone || null,
        organization: data.contact.organization || null,
        category: categoryMap[data.serviceType],
        source: "dugout-website",
        priority: "normal",
        contextual_data: {
          // Include all answers from the form
          ...data.answers,
          // Service type specific data
          serviceType: data.serviceType,
          // Contact extras
          website: data.contact.website || null,
          referralSource: data.contact.referralSource || null,
        },
      };

      // Submit to Supabase
      const { success, error } = await submitInquiry(inquiryData);

      if (error) {
        throw error;
      }

      console.log("Multi-step inquiry submitted successfully");

      // Show success toast
      toast({
        title: "Message sent!",
        description: "Thank you for reaching out. We'll get back to you shortly.",
        variant: "default",
      });

      return true;
    } catch (error) {
      console.error("Error submitting form:", error);

      // Show error toast
      toast({
        title: "Something went wrong",
        description: "Please try again or email us directly.",
        variant: "destructive",
      });

      return false;
    }
  };

  return (
    <div className="bg-[var(--dug-bg-surface)] rounded-2xl shadow-xl p-6 md:p-8 max-w-2xl mx-auto border border-[var(--dug-border)]">
      <MultiStepForm onSubmit={handleSubmit} />
    </div>
  );
};

export default ContactForm;
